// Каталог скважин — CRUD над `/api/public/wells`. OSDU Well, средний уровень
// онтологии Field→Well→Wellbore. По паттерну FieldsPage/CasingsPage (list +
// Editor в одном файле, refetch вместо оптимистики, list↔editor через
// search-параметры ?edit=N / ?new=1).
//
// Поля: name (обяз.), description (текст), field_id (привязка к месторождению —
// `<select>` по каталогу /api/public/fields, пусто = «не привязано»),
// latitude/longitude (координаты устья — опциональны, пусто = NULL).
// uuid/slug генерирует сервер (внутренние хендлы), в форме не редактируются.

import { createSignal, createResource, createMemo, Show, For, onMount, onCleanup } from 'solid-js'
import type { Component } from 'solid-js'
import { Icon } from './Icons'
import {
  listWells,
  createWell,
  updateWell,
  deleteWell,
  InUseError,
} from './api/wells'
import type { WellDto } from './api/wells'
import { listFields } from './api/fields'
import type { FieldDto } from './api/fields'

// ────────────────────── редактор ──────────────────────

type EditorState = {
  /// `null` — создаём. Иначе редактируем.
  source:      WellDto | null
  name:        string
  description: string
  /// Привязка к месторождению. `null` — не привязано.
  fieldId:     number | null
  /// Координаты как строки — текстовое поле, парсим на submit. Пусто = не задано.
  latitude:    string
  longitude:   string
}

function emptyEditor(): EditorState {
  return {
    source:      null,
    name:        '',
    description: '',
    fieldId:     null,
    latitude:    '',
    longitude:   '',
  }
}

// `null` координата (не задана) → пустая строка в форме.
const coordStr = (v: number | null): string => (v == null ? '' : String(v))

function editorFrom(w: WellDto): EditorState {
  return {
    source:      w,
    name:        w.name,
    description: w.description,
    fieldId:     w.field_id,
    latitude:    coordStr(w.latitude),
    longitude:   coordStr(w.longitude),
  }
}

type EditorProps = {
  state:   EditorState
  fields:  FieldDto[]
  onClose: () => void
  onSaved: () => void
}

const Editor: Component<EditorProps> = (props) => {
  const initial = props.state
  const [st, setSt] = createSignal<EditorState>(props.state)
  const [busy, setBusy] = createSignal(false)
  const [err, setErr] = createSignal<string | null>(null)

  const isCreate = () => st().source === null

  const isDirty = createMemo<boolean>(() => {
    const c = st()
    if (isCreate()) {
      return (
        c.name.trim().length > 0 ||
        c.description.trim().length > 0 ||
        c.fieldId != null ||
        c.latitude.trim().length > 0 ||
        c.longitude.trim().length > 0
      )
    }
    return (
      c.name !== initial.name
      || c.description !== initial.description
      || c.fieldId !== initial.fieldId
      || c.latitude !== initial.latitude
      || c.longitude !== initial.longitude
    )
  })

  const tryClose = () => {
    if (busy()) return
    if (isDirty() && !confirm('Несохранённые изменения будут потеряны. Закрыть?')) return
    props.onClose()
  }

  onMount(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        tryClose()
      }
    }
    window.addEventListener('keydown', onKey)
    onCleanup(() => window.removeEventListener('keydown', onKey))
  })

  // Опциональная координата: пусто → null; иначе число в диапазоне [lo, hi].
  // Возвращает `number | null` при успехе, либо строку-сообщение об ошибке.
  const parseCoord = (raw: string, label: string, lo: number, hi: number): number | null | string => {
    const trimmed = raw.trim()
    if (trimmed === '') return null
    const n = Number(trimmed)
    if (!Number.isFinite(n)) return `Поле ${label}: ожидается число`
    if (n < lo || n > hi) return `Поле ${label}: вне диапазона [${lo}, ${hi}]`
    return n
  }

  const onSubmit = async (e: Event) => {
    e.preventDefault()
    setErr(null)
    const cur = st()
    const nameTrimmed = cur.name.trim()
    if (nameTrimmed.length === 0) {
      setErr('Название обязательно')
      return
    }

    const lat = parseCoord(cur.latitude,  'широта',  -90,  90)
    if (typeof lat === 'string') { setErr(lat); return }
    const lon = parseCoord(cur.longitude, 'долгота', -180, 180)
    if (typeof lon === 'string') { setErr(lon); return }

    setBusy(true)
    try {
      if (isCreate()) {
        await createWell({
          name:        nameTrimmed,
          description: cur.description,
          field_id:    cur.fieldId,
          latitude:    lat,
          longitude:   lon,
        })
      } else {
        // fieldId === null → отвязать месторождение (clear_field=true; сервер
        // обнулит колонку, проигнорировав field_id). Иначе — привязать к
        // выбранному.
        await updateWell(cur.source!.id, {
          name:        nameTrimmed,
          description: cur.description,
          field_id:    cur.fieldId,
          clear_field: cur.fieldId === null,
          latitude:    lat,
          longitude:   lon,
        })
      }
      props.onSaved()
    } catch (e) {
      setErr(String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form class="equipment-editor" onSubmit={onSubmit}>
      <div class="equipment-editor-head">
        <button class="btn btn-ghost" type="button" onClick={() => tryClose()} disabled={busy()}>
          ← К каталогу
        </button>
        <div class="equipment-editor-title">
          <div class="uppercase">{isCreate() ? 'Новая скважина' : 'Редактирование'}</div>
          <h1>{isCreate() ? 'Создать скважину' : st().name || 'Скважина'}</h1>
        </div>
        <div class="equipment-editor-actions">
          <button class="btn" type="button" onClick={() => tryClose()} disabled={busy()}>
            Отмена
          </button>
          <button class="btn btn-primary" type="submit" disabled={busy() || !isDirty()}>
            {busy() ? 'Сохраняем…' : isCreate() ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>

      <Show when={err()}>
        <div class="card equipment-error">{err()}</div>
      </Show>

      <main class="equipment-form-panel">
        <section class="equipment-form-section">
          <div class="equipment-form-main">
            <label class="field">
              <div class="field-label">Название</div>
              <input
                class="field-input"
                value={st().name}
                onInput={(e) => setSt({ ...st(), name: e.currentTarget.value })}
                placeholder="Напр., Берёзовская 4"
                autofocus
              />
            </label>

            <label class="field">
              <div class="field-label">Описание</div>
              <textarea
                class="field-input"
                rows={3}
                value={st().description}
                onInput={(e) => setSt({ ...st(), description: e.currentTarget.value })}
                placeholder="Произвольное примечание"
              />
            </label>

            <label class="field">
              <div class="field-label">Месторождение</div>
              <select
                class="field-input"
                value={st().fieldId == null ? '' : String(st().fieldId)}
                onChange={(e) => {
                  const v = e.currentTarget.value
                  setSt({ ...st(), fieldId: v === '' ? null : Number(v) })
                }}
              >
                <option value="">— не привязано —</option>
                <For each={props.fields}>
                  {(f) => <option value={String(f.id)}>{f.name}</option>}
                </For>
              </select>
            </label>
          </div>

          <div class="field">
            <div class="field-label">Координаты устья</div>
            <div class="dim-grid equipment-dim-grid">
              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Широта, °</span>
                  <span class="dim-hint">устье, [-90; 90]</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().latitude}
                  onInput={(e) => setSt({ ...st(), latitude: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="61.05"
                />
              </div>

              <div class="dim-cell">
                <div class="dim-label">
                  <span class="dim-symbol">Долгота, °</span>
                  <span class="dim-hint">устье, [-180; 180]</span>
                </div>
                <input
                  class="field-input mono"
                  value={st().longitude}
                  onInput={(e) => setSt({ ...st(), longitude: e.currentTarget.value })}
                  inputmode="decimal"
                  placeholder="76.8"
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    </form>
  )
}

// ────────────────────── страница ──────────────────────

type WellsPageProps = {
  wellId?:  number | null
  create?:  boolean
  onOpen:   (id: number) => void
  onCreate: () => void
  onBack:   () => void
}

export const WellsPage: Component<WellsPageProps> = (props) => {
  const [items, { refetch }] = createResource(listWells)
  // Каталог месторождений — для селектора привязки и для колонки «Месторождение».
  const [fields] = createResource(listFields)
  const [delErr, setDelErr] = createSignal<string | null>(null)
  const [filterText, setFilterText] = createSignal('')

  const fieldList = createMemo<FieldDto[]>(() => fields() ?? [])
  // id → имя месторождения, для отрисовки привязки в таблице.
  const fieldNameById = createMemo<Record<number, string>>(() => {
    const map: Record<number, string> = {}
    for (const f of fieldList()) map[f.id] = f.name
    return map
  })

  const editor = createMemo<EditorState | null>(() => {
    if (props.create) return emptyEditor()
    if (props.wellId == null) return null
    const item = (items() ?? []).find((w) => w.id === props.wellId)
    return item ? editorFrom(item) : null
  })

  const filtered = createMemo<WellDto[]>(() => {
    const all = items() ?? []
    const q = filterText().trim().toLowerCase()
    if (q.length === 0) return all
    const names = fieldNameById()
    return all.filter((w) =>
      w.name.toLowerCase().includes(q)
      || (w.description ?? '').toLowerCase().includes(q)
      || (w.field_id != null && (names[w.field_id] ?? '').toLowerCase().includes(q))
    )
  })

  const openCreate = () => props.onCreate()
  const openEdit = (w: WellDto) => props.onOpen(w.id)
  const closeEditor = () => props.onBack()
  const onSaved = () => {
    refetch()
    props.onBack()
  }

  const onDelete = async (w: WellDto) => {
    if (!confirm(`Удалить «${w.name}»?`)) return
    setDelErr(null)
    try {
      await deleteWell(w.id)
      refetch()
    } catch (err) {
      if (err instanceof InUseError) setDelErr(err.message)
      else setDelErr(String(err))
    }
  }

  const fieldSummary = (w: WellDto): string =>
    w.field_id == null ? '—' : (fieldNameById()[w.field_id] ?? `#${w.field_id}`)

  const coordsSummary = (w: WellDto): string =>
    w.latitude == null && w.longitude == null
      ? '—'
      : `${w.latitude ?? '?'}, ${w.longitude ?? '?'}`

  return (
    <Show
      when={editor()}
      keyed
      fallback={<div class="page page-wide">
      <div class="page-header">
        <div>
          <h1 class="page-title">Каталог скважин</h1>
          <p class="page-subtitle">
            Скважины (OSDU Well) — средний уровень онтологии
            Field→Well→Wellbore. Привязка к месторождению и координаты устья.
          </p>
        </div>
        <div class="row">
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Скважина
          </button>
        </div>
      </div>

      <Show when={delErr()}>
        <div class="card" style={{ padding: 'var(--sp-3) var(--sp-4)', 'margin-bottom': 'var(--sp-4)', color: 'var(--status-err)' }}>
          {delErr()}
        </div>
      </Show>

      <Show when={items.loading}>
        <div class="card" style={{ padding: 'var(--sp-5)' }}>Загрузка…</div>
      </Show>

      <Show when={items.error}>
        <div class="card" style={{ padding: 'var(--sp-5)', color: 'var(--status-err)' }}>
          Ошибка: {String(items.error)}
        </div>
      </Show>

      <Show when={items() && items()!.length === 0}>
        <div class="card" style={{ padding: 'var(--sp-7)', 'text-align': 'center' }}>
          <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)' }}>
            Каталог пуст
          </div>
          <div class="muted text-sm" style={{ 'margin-bottom': 'var(--sp-4)' }}>
            Создайте первую скважину
          </div>
          <button class="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" />Добавить скважину
          </button>
        </div>
      </Show>

      <Show when={items() && items()!.length > 0}>
        <div class="row filter-row" style={{ gap: 'var(--sp-3)', 'margin-bottom': 'var(--sp-4)' }}>
          <div class="input" style={{ flex: 1, 'max-width': '320px' }}>
            <Icon name="search" />
            <input
              placeholder="Поиск по названию / месторождению"
              value={filterText()}
              onInput={(e) => setFilterText(e.currentTarget.value)}
            />
            <Show when={filterText().length > 0}>
              <button
                class="btn-icon btn-ghost"
                style={{ width: '24px', height: '24px' }}
                title="Очистить"
                onClick={() => setFilterText('')}
              >
                <Icon name="x" size={12} />
              </button>
            </Show>
          </div>
          <div class="muted text-sm filter-counter" style={{ 'margin-left': 'auto', 'align-self': 'center' }}>
            Показано {filtered().length} из {items()!.length}
          </div>
        </div>

        <div class="card">
          <table class="tbl">
            <thead>
              <tr>
                <th style={{ width: '110px' }}>ID</th>
                <th>Название</th>
                <th>Месторождение</th>
                <th>Координаты устья</th>
                <th>Описание</th>
                <th style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody>
              <For each={filtered()}>
                {(w) => (
                  <tr style={{ cursor: 'pointer' }} onClick={() => openEdit(w)}>
                    <td class="mono text-xs muted">#{w.id}</td>
                    <td style={{ 'font-weight': 500 }}>{w.name}</td>
                    <td class="text-xs">{fieldSummary(w)}</td>
                    <td class="mono text-xs">{coordsSummary(w)}</td>
                    <td class="text-xs muted">{w.description || '—'}</td>
                    <td>
                      <button
                        class="btn-icon"
                        title="Удалить"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          onDelete(w)
                        }}
                      >
                        <Icon name="x" />
                      </button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={filtered().length === 0}>
            <div style={{ padding: 'var(--sp-5)', 'text-align': 'center' }} class="muted text-sm">
              Ничего не найдено по фильтру
            </div>
          </Show>
        </div>
      </Show>

      </div>}
    >
      {(ed) => (
        <div class="page page-wide">
          <Editor state={ed} fields={fieldList()} onClose={closeEditor} onSaved={onSaved} />
        </div>
      )}
    </Show>
  )
}
